import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AlertProvider, AIProcessingProvider, AnticipatoryProvider, ViewOnlyProvider, PatientPanelProvider } from './contexts';
import PatientQuickPanel from './components/patient/PatientQuickPanel';
import ProductionBanner from './components/layout/ProductionBanner';
import { InteractiveSentinelBear } from './components/mascot';
import { LoadingScreen, RouteLoadingSpinner, ErrorBoundary } from './components/ui';
import { ProfileModal, PasswordModal, UsersModal, AuditModal, SMSModal, BookingModal } from './components/modals';
import IntakeForm from './components/IntakeForm';
import MorningBoard from './components/MorningBoard';
import AIAssistantWidget from './components/AIAssistantWidget';
import AppShell from './components/layout/AppShell';
import { PersistentDrawer } from './components/layout/PersistentDrawer';
import { useAppNavigate } from './hooks/useAppNavigate';
import './App.css';

// Lazy-loaded view components
const ClinicView = lazy(() => import('./components/Dashboard/ClinicView'));
const WaitingRoomPipeline = lazy(() => import('./components/Dashboard/WaitingRoomPipeline'));
const CoordinationHub = lazy(() =>
  import('./components/coordination').then((m) => ({ default: m.CoordinationHub }))
);
const PatientsView = lazy(() => import('./components/views/PatientsView'));
const AIHubView = lazy(() => import('./components/views/AIHubView'));
const SettingsView = lazy(() => import('./components/views/SettingsView'));
const NotificationsView = lazy(() => import('./components/views/NotificationsView'));
const BillingView = lazy(() => import('./components/views/BillingView'));
const RecallView = lazy(() => import('./components/views/RecallView'));
const ReportsView = lazy(() => import('./components/views/ReportsView'));
const InventoryView = lazy(() => import('./components/views/InventoryView'));
const TasksView = lazy(() => import('./components/views/TasksView'));
const DecisionaryView = lazy(() => import('./components/views/DecisionaryView'));
const QADashboard = lazy(() => import('./components/views/QADashboard'));
const MedicationReference = lazy(() => import('./components/views/MedicationReference'));
const ProcedureGuide = lazy(() => import('./components/views/ProcedureGuide'));
const NotFound = lazy(() => import('./components/views/NotFound'));

function App() {
  // Language state
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('dashboardLang');
    return saved || 'he';
  });

  // Modal states for user menu actions
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Navigation helper (maps view IDs to URL paths)
  const appNavigate = useAppNavigate();

  // Save language preference
  useEffect(() => {
    localStorage.setItem('dashboardLang', language);
  }, [language]);

  return (
    <div className="app-root" dir={language === 'he' ? 'rtl' : 'ltr'}>
      <ViewOnlyProvider>
      <PatientPanelProvider>
      <ProductionBanner language={language} />
      <AlertProvider>
      <AIProcessingProvider>
      <AnticipatoryProvider>
      <ErrorBoundary language={language}>
        <Suspense fallback={<RouteLoadingSpinner />}>
          <Routes>
            <Route
              element={
                <AppShell
                  language={language}
                  onLanguageToggle={() => setLanguage(language === 'en' ? 'he' : 'en')}
                  onProfileClick={() => setShowProfileModal(true)}
                  onPasswordClick={() => setShowPasswordModal(true)}
                  onUsersClick={() => setShowUsersModal(true)}
                  onAuditClick={() => setShowAuditModal(true)}
                />
              }
            >
              <Route
                index
                element={
                  <ClinicView
                    language={language}
                    onNavigate={appNavigate}
                  />
                }
              />
              <Route
                path="calendar"
                element={
                  <PersistentDrawer language={language} onNavigate={appNavigate}>
                    <CoordinationHub
                      language={language}
                      onNewAppointment={() => setShowBookingModal(true)}
                      onNavigate={appNavigate}
                    />
                  </PersistentDrawer>
                }
              />
              <Route
                path="waiting-room"
                element={
                  <PersistentDrawer language={language} onNavigate={appNavigate}>
                    <WaitingRoomPipeline language={language} />
                  </PersistentDrawer>
                }
              />
              <Route
                path="patients"
                element={
                  <PersistentDrawer language={language} onNavigate={appNavigate}>
                    <PatientsView language={language} />
                  </PersistentDrawer>
                }
              />
              <Route path="recalls" element={<RecallView language={language} />} />
              <Route path="billing" element={<BillingView language={language} />} />
              <Route path="reports" element={<ReportsView language={language} />} />
              <Route path="ai-hub" element={<AIHubView language={language} />} />
              <Route path="inventory" element={<InventoryView language={language} />} />
              <Route path="tasks" element={<TasksView language={language} />} />
              <Route path="decisionary" element={<DecisionaryView language={language} />} />
              <Route path="qa" element={<QADashboard language={language} />} />
              <Route path="settings" element={<SettingsView language={language} />} />
              <Route path="notifications" element={<NotificationsView language={language} />} />
              <Route path="medications" element={<MedicationReference language={language} />} />
              <Route path="procedures" element={<ProcedureGuide language={language} />} />
              <Route path="*" element={<NotFound language={language} />} />
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        language={language}
      />

      {/* Password Modal */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        language={language}
      />

      {/* Users Management Modal */}
      <UsersModal
        isOpen={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        language={language}
      />

      {/* Audit Log Modal */}
      <AuditModal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        language={language}
      />

      {/* SMS Modal */}
      <SMSModal
        isOpen={showSMSModal}
        onClose={() => setShowSMSModal(false)}
        language={language}
      />

      {/* Booking Modal */}
      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        language={language}
      />

      {/* AI Assistant Widget - Global floating chat */}
      <AIAssistantWidget language={language} />

      {/* Sentinel Bear Mascot — now also the AI assistant's opener */}
      <InteractiveSentinelBear
        onClick={() => window.dispatchEvent(new Event('taisacore:toggle-assistant'))}
      />

      {/* Global patient slide-over — opened by PatientLink from any screen */}
      <PatientQuickPanel language={language} />
      </AnticipatoryProvider>
      </AIProcessingProvider>
      </AlertProvider>
      </PatientPanelProvider>
      </ViewOnlyProvider>
    </div>
  );
}

// App Content wrapper that handles authentication
function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen message="טוען..." />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public route — IntakeForm (no auth required) */}
        <Route path="/intake" element={<IntakeForm />} />

        {/* Morning board — chrome-free glance screen for the front desk,
            meant to be auto-opened at Windows login. Auth-gated like
            everything else; deliberately OUTSIDE the app shell so it
            carries no sidebar/header to read past. */}
        <Route
          path="/morning"
          element={isAuthenticated ? <MorningBoard /> : <LoginPage />}
        />

        {/* All other routes — auth required */}
        <Route
          path="/*"
          element={isAuthenticated ? <App /> : <LoginPage />}
        />
      </Routes>
    </BrowserRouter>
  );
}

// Root component with AuthProvider
function AppRoot() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default AppRoot;
